/** Shared promotion rule helpers (pricing + public listing). */

export type PromotionType =
  | "last_minute_rate"
  | "min_nights_schedule"
  | "display"
  | "seasonal_rate";

/** Shape returned by GET /api/promotions (admin promos + seasonal rates). */
export interface PublicPromotionCard {
  id: string;
  type: PromotionType | string;
  title: string;
  terms: string;
  config?: DisplayPromoConfig & Record<string, unknown>;
  sort_order: number;
}

export interface LastMinuteRateConfig {
  nightlyRate: number;
  /**
   * The window, as a rolling number of calendar months from today: 1 means any
   * check-in from today through the same date next month. When set it wins
   * over leadDays, which older rows carry.
   */
  leadMonths?: number;
  leadDays: number;
  stackWithDiscounts: boolean;
}

export interface MinNightsScheduleConfig {
  nearMonths: number;
  nearMinNights: number;
  farMinNights: number;
}

export interface DisplayPromoConfig {
  couponCode?: string;
  ctaHref?: string;
  /** "link" (default) or "email_offer" to open the discount lightbox */
  ctaAction?: "link" | "email_offer";
  ctaLabel?: string;
}

export type PromotionConfig =
  | LastMinuteRateConfig
  | MinNightsScheduleConfig
  | DisplayPromoConfig
  | Record<string, unknown>;

export const DEFAULT_LAST_MINUTE: LastMinuteRateConfig = {
  nightlyRate: 499,
  leadMonths: 1,
  leadDays: 6,
  stackWithDiscounts: false,
};

export const DEFAULT_MIN_NIGHTS_SCHEDULE: MinNightsScheduleConfig = {
  nearMonths: 1,
  nearMinNights: 4,
  farMinNights: 6,
};

/** Hawaii-local calendar date as YYYY-MM-DD. */
export function hawaiiTodayYmd(now: Date = new Date()): string {
  return now.toLocaleDateString("en-CA", { timeZone: "Pacific/Honolulu" });
}

export interface FallPromo2026 {
  propertySlug: string;
  homeTitle: string;
  /** First eligible NIGHT of the stay. */
  startsOn: string;
  /** Last eligible NIGHT of the stay. */
  endsOn: string;
  /** The standard nightly rate this offer is measured against. */
  regularNightly: number;
  promoNightly: number;
}

/**
 * The limited-time fall offer, one entry per home.
 *
 * Written down once, here, because the same numbers appear in the page copy, in
 * the structured data, in the newsletter draft, and in the seasonal pricing
 * rule that makes the offer real at checkout. A promotional price quoted in
 * marketing that the booking engine does not actually charge is the failure
 * worth designing against, so the displayed rate and the priced rate resolve
 * from this one definition.
 *
 * Each home is keyed by slug and carries its own rates, measured against that
 * home's own standard nightly rate. Both prices went up $100 on 2026-09-30
 * with the standard rates (owner's request), so the percentage off, which is
 * derived, fell from 27% to 23% (Kualoa) and 24% (Caldwell).
 */
export const KUALOA_FALL_PROMO: FallPromo2026 = {
  propertySlug: "the-kualoa-beach-house",
  homeTitle: "The Kualoa Beach House",
  startsOn: "2026-09-14",
  endsOn: "2026-10-14",
  regularNightly: 749,
  promoNightly: 573.99,
};

export const CALDWELL_FALL_PROMO: FallPromo2026 = {
  propertySlug: "caldwell-beach-house",
  homeTitle: "The Caldwell Beach House",
  startsOn: KUALOA_FALL_PROMO.startsOn,
  endsOn: KUALOA_FALL_PROMO.endsOn,
  regularNightly: 849,
  promoNightly: 646.99,
};

export const FALL_PROMOS_2026: readonly FallPromo2026[] = [KUALOA_FALL_PROMO, CALDWELL_FALL_PROMO];

/** The fall offer for a home, or null when that home has none. */
export function fallPromoForSlug(slug: string | null | undefined): FallPromo2026 | null {
  if (!slug) return null;
  // The first home's old slug still reaches its offer.
  const canonical = slug === "kualoa-beach-house" ? KUALOA_FALL_PROMO.propertySlug : slug;
  return FALL_PROMOS_2026.find((p) => p.propertySlug === canonical) ?? null;
}

/**
 * How much an offer actually saves, derived rather than written down.
 *
 * This was a hardcoded 5, which was measured against $499 - itself a
 * promotional rate, not the standard one. Stating a percentage next to two
 * prices that imply a different percentage reads as a pricing error and
 * undersells the offer, so the number is computed from the two rates and cannot
 * disagree with them.
 */
export function fallPromoPercentOff(promo: FallPromo2026 = KUALOA_FALL_PROMO): number {
  return Math.round((1 - promo.promoNightly / promo.regularNightly) * 100);
}

export function kualoaFallPromoPercentOff(): number {
  return fallPromoPercentOff(KUALOA_FALL_PROMO);
}

/**
 * Whether the offer should still be advertised.
 *
 * True until the last eligible night has passed, including before the window
 * opens - the offer is for future stays, so it has to be visible while it can
 * still be booked. On October 15, 2026 this turns false on its own and both
 * placements stop rendering; nothing needs to be deleted by hand.
 *
 * Evaluated against the Hawaii calendar date, matching every other date
 * decision in this file, so the offer does not linger or vanish early for
 * visitors in other time zones.
 */
export function isKualoaFallPromoVisible(todayYmd: string = hawaiiTodayYmd()): boolean {
  return todayYmd <= KUALOA_FALL_PROMO.endsOn;
}

/** True when a given stay night falls inside the promotional window. */
export function isKualoaFallPromoNight(nightYmd: string): boolean {
  return nightYmd >= KUALOA_FALL_PROMO.startsOn && nightYmd <= KUALOA_FALL_PROMO.endsOn;
}

export function addDaysYmd(ymd: string, days: number): string {
  const dt = new Date(`${ymd}T12:00:00Z`);
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export function addMonthsYmd(ymd: string, months: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  // Clamp at month end rather than overflowing (Jan 31 + 1 month = Feb 28/29).
  const target = new Date(Date.UTC(y, m - 1 + months, 1, 12));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0, 12)).getUTCDate();
  const dt = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), Math.min(d, lastDay), 12));
  return dt.toISOString().slice(0, 10);
}

export function isLastMinuteCheckIn(
  checkInYmd: string,
  leadDays: number,
  todayYmd: string = hawaiiTodayYmd(),
): boolean {
  if (!checkInYmd || leadDays < 0) return false;
  const cutoff = addDaysYmd(todayYmd, leadDays);
  return checkInYmd >= todayYmd && checkInYmd <= cutoff;
}

/** Last check-in date (inclusive) the last-minute rate covers, from today. */
export function lastMinuteCutoffYmd(
  config: Pick<LastMinuteRateConfig, "leadMonths" | "leadDays">,
  todayYmd: string = hawaiiTodayYmd(),
): string {
  return config.leadMonths && config.leadMonths > 0
    ? addMonthsYmd(todayYmd, config.leadMonths)
    : addDaysYmd(todayYmd, Math.max(0, config.leadDays || 0));
}

/** Whether a check-in on this date gets the last-minute rate today. */
export function isLastMinuteCheckInFor(
  checkInYmd: string,
  config: Pick<LastMinuteRateConfig, "leadMonths" | "leadDays">,
  todayYmd: string = hawaiiTodayYmd(),
): boolean {
  if (!checkInYmd) return false;
  return checkInYmd >= todayYmd && checkInYmd <= lastMinuteCutoffYmd(config, todayYmd);
}

export function resolveMinNightsForCheckIn(
  checkInYmd: string,
  config: MinNightsScheduleConfig,
  todayYmd: string = hawaiiTodayYmd(),
): number {
  const near = Math.max(1, config.nearMinNights || 1);
  const far = Math.max(1, config.farMinNights || near);
  const months = Math.max(0, config.nearMonths || 0);
  if (months === 0) return far;
  const cutoff = addMonthsYmd(todayYmd, months);
  return checkInYmd <= cutoff ? near : far;
}

/** Whether an enclosed open run has at least one valid arrival date. */
export function hasBookableCheckInInRun(
  runDates: string[],
  minNightsFor: (checkInYmd: string) => number,
): boolean {
  return runDates.some((date, index) => runDates.length - index >= minNightsFor(date));
}

export function parseLastMinuteConfig(raw: unknown): LastMinuteRateConfig {
  const c = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    nightlyRate: Number(c.nightlyRate ?? DEFAULT_LAST_MINUTE.nightlyRate) || DEFAULT_LAST_MINUTE.nightlyRate,
    // A row that names neither falls back to the default window (one month).
    leadMonths: c.leadMonths != null
      ? Math.max(0, Number(c.leadMonths) || 0) || undefined
      : c.leadDays != null ? undefined : DEFAULT_LAST_MINUTE.leadMonths,
    leadDays: Number(c.leadDays ?? DEFAULT_LAST_MINUTE.leadDays) || DEFAULT_LAST_MINUTE.leadDays,
    stackWithDiscounts: Boolean(c.stackWithDiscounts ?? DEFAULT_LAST_MINUTE.stackWithDiscounts),
  };
}

export function parseMinNightsConfig(raw: unknown): MinNightsScheduleConfig {
  const c = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    nearMonths: Number(c.nearMonths ?? DEFAULT_MIN_NIGHTS_SCHEDULE.nearMonths) || DEFAULT_MIN_NIGHTS_SCHEDULE.nearMonths,
    nearMinNights:
      Number(c.nearMinNights ?? DEFAULT_MIN_NIGHTS_SCHEDULE.nearMinNights) ||
      DEFAULT_MIN_NIGHTS_SCHEDULE.nearMinNights,
    farMinNights:
      Number(c.farMinNights ?? DEFAULT_MIN_NIGHTS_SCHEDULE.farMinNights) ||
      DEFAULT_MIN_NIGHTS_SCHEDULE.farMinNights,
  };
}

export function isPromotionVisibleNow(
  startsOn: string | null | undefined,
  endsOn: string | null | undefined,
  todayYmd: string = hawaiiTodayYmd(),
): boolean {
  if (startsOn && todayYmd < startsOn) return false;
  if (endsOn && todayYmd > endsOn) return false;
  return true;
}

/** Seasonal rates remain listed until their end date passes (includes upcoming windows). */
export function isSeasonalRateListedNow(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  todayYmd: string = hawaiiTodayYmd(),
): boolean {
  if (!startDate || !endDate) return false;
  return todayYmd <= endDate;
}

function formatPromoMoney(amount: number): string {
  if (!Number.isFinite(amount)) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatPromoDate(ymd: string): string {
  const dt = new Date(`${ymd}T12:00:00Z`);
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Build a public Current Promotions card from a seasonal pricing rule. */
export function seasonalRuleToPublicPromotion(rule: {
  id: string;
  value: string | number;
  start_date: string | null;
  end_date: string | null;
  sortIndex?: number;
}): PublicPromotionCard {
  const rate = typeof rule.value === "number" ? rule.value : parseFloat(String(rule.value));
  const start = rule.start_date || "";
  const end = rule.end_date || "";
  const rateLabel = formatPromoMoney(rate);
  return {
    id: `seasonal-${rule.id}`,
    type: "seasonal_rate",
    title: `Seasonal rate: ${rateLabel}/night`,
    terms: `Book nights from ${formatPromoDate(start)} to ${formatPromoDate(end)} at ${rateLabel}/night when you book direct.`,
    config: {
      ctaHref: "/booking",
      ctaAction: "link",
      ctaLabel: "Check dates",
      nightlyRate: rate,
      startDate: start,
      endDate: end,
    },
    // Keep seasonal offers near the top of the public list.
    sort_order: rule.sortIndex ?? 0,
  };
}

/** Advertise only nightly windows that the existing narrowest-window quote rule wins.
 * This normalizes public cards without changing stored rules or quote amounts.
 */
export function effectiveSeasonalWindows(rules: Array<{id:string; rule_type:string; value:string; percentage:boolean | null; start_date:string | null; end_date:string | null}>, today:string) {
  const seasonal = rules.filter(r => r.rule_type === 'seasonal' && !r.percentage && r.start_date && r.end_date && r.end_date >= today)
    .sort((a,b) => (Date.parse(a.end_date!) - Date.parse(a.start_date!)) - (Date.parse(b.end_date!) - Date.parse(b.start_date!)) || a.start_date!.localeCompare(b.start_date!));
  const boundaries = [...new Set([today, ...seasonal.flatMap(r => [r.start_date! < today ? today : r.start_date!, addDaysYmd(r.end_date!, 1)])])].sort();
  const segments: Array<{id:string; value:string; start_date:string; end_date:string}> = [];
  for (let i=0; i<boundaries.length-1; i++) {
    const start=boundaries[i], end=addDaysYmd(boundaries[i+1],-1);
    const winner=seasonal.find(r => r.start_date! <= start && r.end_date! >= start);
    if (!winner) continue;
    const prior=segments[segments.length-1];
    if (prior && Number(prior.value)===Number(winner.value) && addDaysYmd(prior.end_date,1)===start) prior.end_date=end;
    else segments.push({id:winner.id+'-'+start,value:winner.value,start_date:start,end_date:end});
  }
  return segments;
}
